import moment from 'moment';
import * as React from 'react';
import { Overlay, Popover } from 'react-bootstrap';

import { DateRange, OnChangeProps } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css';
import ReactDOM from 'react-dom';
import { EventPopupContent } from '../../../containers/calendar/components/event-popup-content';
import { DateFormats, formatDate } from '../../utils/date-utils';
import {
  IBigCalendarEvent,
  IBigCalendarResource,
} from './big-calendar-component';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { start } from 'repl';
import { useCalendarContext } from '../../hooks/useCalendarContext';
import { capitalize, map, words } from 'lodash';

interface ICustomWeekViewProps {
  events: IBigCalendarEvent[];
  resourceIdAccessor: string;
  resourceTitleAccessor: string;
  resources: IBigCalendarResource[];
  range: Date[];
}

export const CustomWeekView: React.FunctionComponent<ICustomWeekViewProps> = (
  props
) => {
  const events = props.events;
  const {
    updateEditedEvent: updateEvent,
    setSelectedDate,
    setCurrentView,
  } = useCalendarContext();

  const renderEvents = (date: Date, resourceId: string, col: number) => {
    const filteredEvents = events.filter((p) => p.resourceId === resourceId);
    const assignedEvents: IBigCalendarEvent[] = [];
    filteredEvents.forEach((e) => {
      const currentDate = moment(date);
      const eventStartDate = moment(e.start);
      const eventEndDate = moment(e.end);
      if (
        eventStartDate.isSame(currentDate, 'day') ||
        eventEndDate.isSame(currentDate, 'day') ||
        currentDate.isBetween(eventStartDate, eventEndDate)
      ) {
        assignedEvents.push(e);
      }
    });

    return (
      <div>
        {assignedEvents.map((p, i) => {
          if (p.activityType === 'timeOff') {
            return <CalendarEventItem event={p}></CalendarEventItem>;
          }
          return (
            <Draggable
              key={createDraggableKey(p, col)}
              draggableId={createDraggableKey(p, col)}
              index={i}
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  data-for={p}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                >
                  <CalendarEventItem event={p}></CalendarEventItem>
                </div>
              )}
            </Draggable>
          );
        })}
      </div>
    );
  };

  const createDraggableKey = (e: IBigCalendarEvent, col: number) => {
    return `draggable-${e.id}-${col}`;
  };

  const createDroppableKey = (
    row: number,
    column: number,
    resourceId: string
  ) => {
    return `droppable-${row}-${column}-${resourceId}`;
  };

  const getActivityCountForDay = (date: Date) => {
    const count = events.filter(
      (p) => p.resourceId !== '' && moment(date).isSame(p.start, 'day')
    ).length;
    return (
      <div className="font-xxs pb-1 text-gray-500">
        {count} {count > 1 ? 'Activities' : 'Activity'}
      </div>
    );
  };

  const onDragEnd = (result: DropResult) => {
    getEventDetailsFromDropResult(result);
  };

  const getEventDetailsFromDropResult = (result: DropResult) => {
    const key = result.draggableId;
    const sourceSplit = key.split('-');
    const destKey = result?.destination?.droppableId ?? '';
    const destSplit = destKey.split('-');

    if (sourceSplit.length === 3 && destSplit.length === 4) {
      const eventId = sourceSplit[1];
      const sourceColumn = sourceSplit[2];
      const destColumn = destSplit[2];
      const dayDiff = parseInt(destColumn, 10) - parseInt(sourceColumn, 10);
      const eventIndex = events.findIndex((p) => p.id === eventId);

      const resourceId = destSplit[3];
      if (eventIndex !== -1) {
        const event = events[eventIndex];
        const clone = { ...event };
        clone.start = moment(clone.start).add(dayDiff, 'day').toDate();
        clone.end = moment(clone.end).add(dayDiff, 'day').toDate();
        clone.resourceId = resourceId;
        updateEvent(clone, eventIndex);
      }
    }
    return null;
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="table-responsive grid-table">
        <table className="table table-bordered weekday-table">
          <thead>
            <th className="border-bottom weekday-user"></th>
            {props.range.map((p, index) => {
              return (
                <th
                  onClick={() => {
                    setSelectedDate(p);
                    setCurrentView('day');
                  }}
                  className="text-center py-3 font-weight-normal border-bottom cursor-pointer"
                  key={'week-view-header-' + index}
                >
                  <div className="mb-1 pt-1 font-normal text-gray-800">
                    {' '}
                    {formatDate(p, DateFormats.DayNameOnly)}{' '}
                    {formatDate(p, 'DD')}
                  </div>
                  {getActivityCountForDay(p)}
                </th>
              );
            })}
          </thead>
          <tbody>
            {props.resources?.map((p, row) => {
              return (
                <tr key={'week-view-resource-column-' + p.resourceId + row}>
                  <td className="text-center weekday-user">
                    <div className="avatar--sm rounded-circle  mb-2 d-inline-block">
                      <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 32.3789C11.9818 32.3789 8.13931 30.8838 5.18037 28.169C1.888 25.1462 0 20.849 0 16.3789C0 7.55661 7.17771 0.378906 16 0.378906C24.8223 0.378906 32 7.55661 32 16.3789C32 20.3073 30.5651 24.0827 27.9597 27.0094C24.9199 30.4219 20.5612 32.3789 16 32.3789ZM16 1.71224C7.91275 1.71224 1.33333 8.29165 1.33333 16.3789C1.33333 20.4766 3.06379 24.4157 6.08203 27.1865C8.7936 29.6751 12.3164 31.0456 16 31.0456C20.181 31.0456 24.1771 29.2513 26.9635 26.1227C29.3516 23.4405 30.6667 19.9799 30.6667 16.3789C30.6667 8.29165 24.0873 1.71224 16 1.71224Z" fill="#B7B7B7" />
                        <path d="M15.9989 18.3865C19.3163 18.3865 22.0056 15.6972 22.0056 12.3798C22.0056 9.06235 19.3163 6.37305 15.9989 6.37305C12.6815 6.37305 9.99219 9.06235 9.99219 12.3798C9.99219 15.6972 12.6815 18.3865 15.9989 18.3865Z" fill="#B7B7B7" />
                        <path d="M26.6861 24.3403C26.466 24.647 26.2194 24.947 25.9661 25.2336C23.4327 28.0803 19.7993 29.7136 15.9993 29.7136C12.6527 29.7136 9.44606 28.467 6.986 26.207C6.36606 25.6404 5.81267 25.0203 5.31934 24.3536C6.45928 21.5337 8.65267 19.2804 11.3593 18.0603C12.626 19.0936 14.2393 19.7203 15.9993 19.7203C17.7661 19.7203 19.386 19.0936 20.6527 18.0469C21.9526 18.6268 23.1526 19.4602 24.1793 20.5203C25.266 21.6269 26.1127 22.9203 26.6861 24.3403Z" fill="#B7B7B7" />
                      </svg>

                    </div>
                    <div className="text-truncate font-xs text-gray-800">
                      {' '}
                      {p.resourceTitle}
                    </div>
                  </td>

                  {props.range.map((q, column) => {
                    return (
                      <Droppable
                        droppableId={createDroppableKey(
                          row,
                          column,
                          p.resourceId
                        )}
                      >
                        {(provided, snapshot) => (
                          <td
                            ref={provided.innerRef}
                            className="p-2 align-baseline"
                            key={createDroppableKey(row, column, p.resourceId)}
                            style={{
                              minWidth: snapshot.isDraggingOver
                                ? 178
                                : undefined,
                            }}
                          >
                            {/* {renderEvents(q, p.resourceId, column)} */}
                            <GridSystem date={q} resourceId={p.resourceId} col={column} events={events}/>
                          </td>
                        )}
                      </Droppable>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DragDropContext>
  );
};

export const CalendarEventItem = (props: { event: IBigCalendarEvent }) => {
  const event = props.event;
  const [showTooltip, setShowTooltip] = React.useState(false);

  const closeTooltip = () => {
    const x: any = document.getElementById('week-view-popup');
    x.style.display = 'none';
    setShowTooltip(false);
  };

  const openTooltip = () => {
    setShowTooltip(true);
  };
  const ref = React.useRef(null);

  const getTarget = (): any => {
    return ReactDOM.findDOMNode(ref.current);
  };

  return (
    <div
      ref={ref}
      onClick={openTooltip}
      style={{
        backgroundColor: '#' + event.displayDetails.color,
        borderLeft: '3px solid rgba(0,0,0,0.15)',
      }}
      className="weekend-activity position-relative row px-2 py-1 no-gutters"
    >

      <EventItemDesign currentView="week" event={event}></EventItemDesign>

      <Overlay
        rootClose
        target={getTarget}
        show={showTooltip}
        flip
        placement="right"
        onHide={closeTooltip}
      >
        <Popover id="week-view-popup">
          {/* <Popover.Title as="h3">{` ${event.title}`}</Popover.Title> */}
          <Popover.Content>
            <EventPopupContent
              event={event}
              onClose={closeTooltip}
              customerId={event.appointment?.customer?._id}
            />
          </Popover.Content>
        </Popover>
      </Overlay>
    </div>
  );
};

export const EventItemDesign = (props: {
  event: IBigCalendarEvent;
  currentView: string;
}) => {
  const event = props.event;

  return (
    <React.Fragment>
      <div className="col content">
        {props.currentView !== 'month' && (
          <React.Fragment>
            {event.displayDetails.isDraggable && (
              <div className="font-xxs font-weight-semibold text-gray-800 mb-1">
                {formatDate(event.start, DateFormats.TimeOnlyFormat)} -{' '}
                {formatDate(event.end, DateFormats.TimeOnlyFormat)}
              </div>
            )}
            {!event.displayDetails.isDraggable && (
              <div className="font-xxs font-weight-semibold text-gray-800 mb-1">
                {formatDate(
                  event.appointment.startDate,
                  DateFormats.TimeWithDayName
                )}{' '}
                -{' '}
                {formatDate(
                  event.appointment.endDate ?? event.appointment.startDate,
                  DateFormats.TimeWithDayName
                )}
              </div>
            )}
          </React.Fragment>
        )}
        <div className="font-xxs font-weight-semibold text-gray-800">
          {event.title} <span className="font-weight-normal"></span>
        </div>
      </div>
      <div className="col-auto pl-2">
        <div className="activity-type rounded-circle d-flex align-items-center justify-content-center">
          <i className={event.displayDetails.icon + ' font-xxs'}></i>
        </div>
        {(event.appointment?.isCompleted ?? false) &&
          props.currentView !== 'month' && (
            <i className="icon-tick-round text-success font-sm"></i>
          )}
      </div>
    </React.Fragment>
  );
};

export const EventItem  = (props: {event:IBigCalendarEvent}) =>{
  const event = props.event;
  const [showTooltip, setShowTooltip] = React.useState(false);

  const closeTooltip = () => {
    const x: any = document.getElementById('week-view-popup');
    x.style.display = 'none';
    setShowTooltip(false);
  };

  const openTooltip = () => {
    setShowTooltip(true);
  };
  const ref = React.useRef(null);

  const getTarget = (): any => {
    return ReactDOM.findDOMNode(ref.current);
  };

  const getIconByActivity = (activity: string) => {
    if (activity === 'call') {
        return "icon-call";
    } else if (activity === 'deadline') {
      return "icon-flag-fill ";
    } else if (activity === 'meeting') {
      return "icon-users-fill";
    } else if (activity === 'task') {
        return "icon-clock ";
    } else if (activity === 'phoneIn') {
        return "icon-phone_callback ";
    } else if (activity === 'phoneOut') {
        return "icon-phone_forwarded ";
    } else if (activity === 'emailIn') {
        return "icon-emailin";
    } else if (activity === 'emailOut') {
        return "icon-emailout";
    } else if (activity === 'letterIn') {
        return "icon-letterin";
    } else if (activity === 'letterOut') {
        return "icon-letterout ";
    } else if (activity === 'faxIn') {
        return "icon-faxin ";
    } else if (activity === 'faxOut') {
        return "icon-faxout ";
    } else if (activity === 'coldCalling') {
        return "icon-coldcalling ";
    } else if (activity === 'toDo') {
        return "icon-lead ";
    }  else if (activity === 'scheduleASiteVisit') {
        return "icon-building ";
    } else {
        return "";
    }
  }

  function camelCaseToNormal(camelCaseString:string) {
    const strings = words(camelCaseString);
  
    const capitalizedWords = map(strings, capitalize);
  
    return capitalizedWords.join(' ');
  }

  return(
    <div className="innerGsystem" ref={ref}
       onClick={openTooltip}>
      <i data-toggle="tooltip" title={camelCaseToNormal(event.activityType)} className={getIconByActivity(event.activityType)}></i>
      <span>{'(5)'}</span>

      <Overlay
        rootClose
        target={getTarget}
        show={showTooltip}
        flip
        placement="right"
        onHide={closeTooltip}
      >
        <Popover id="week-view-popup">
          {/* <Popover.Title as="h3">{` ${event.title}`}</Popover.Title> */}
          <Popover.Content>
            <EventPopupContent
              event={event}
              onClose={closeTooltip}
              customerId={event.appointment?.customer?._id}
            />
          </Popover.Content>
        </Popover>
      </Overlay>
    </div>
  )
}

export const GridSystem = (props: {date: Date, resourceId: string, col: number, events:IBigCalendarEvent[]}) => {
  const filteredEvents = props.events.filter((p) => p.resourceId === props.resourceId);
  const assignedEvents: IBigCalendarEvent[] = [];
  filteredEvents.forEach((e) => {
    const currentDate = moment(props.date);
    const eventStartDate = moment(e.start);
    const eventEndDate = moment(e.end);
    if (
      eventStartDate.isSame(currentDate, 'day') ||
      eventEndDate.isSame(currentDate, 'day') ||
      currentDate.isBetween(eventStartDate, eventEndDate)
    ) {
      assignedEvents.push(e);
    }
  });

  return (
    <>
    <div className='gridHead'>
      {/* <span>30</span> */}
    </div>
    {/* use 'monthGrid' for  month view */}
      <div className='iconGridSystem'>
        {assignedEvents.map((item: IBigCalendarEvent)=>
          <EventItem event={item}/>
        )}
      </div>
    </>
  )
}